import Settings from "./Settings";
import { AuthToken } from "./AuthToken";

import ProcessingResult from "../framework/ProcessingResult";

import CustomerFragmentDto from "../classes/dtos/metaapimodule/CustomerFragmentDto";
import ApiFragmentDto from "../classes/dtos/metaapimodule/ApiFragmentDto";
import PathFragmentDto, { PathFragmentOperations } from "../classes/dtos/metaapimodule/PathFragmentDto";

const MetaApiApi = {

    OperationsToBody: function (operations: PathFragmentOperations) {
        return {
            'readEnabled': operations.ReadEnabled,
            'addEnabled': operations.AddEnabled,
            'addMultipleEnabled': operations.AddMultipleEnabled,
            'updateEnabled': operations.UpdateEnabled,
            'updateMultipleEnabled': operations.UpdateMultipleEnabled,
            'deleteEnabled': operations.DeleteEnabled,
            'deleteMultipleEnabled': operations.DeleteMultipleEnabled
        };
    },

    GetMetaApiSettingsAsync: async function (): Promise<ProcessingResult<CustomerFragmentDto | null>> {
        let authSession = await AuthToken.get();

        const response = await fetch(Settings.api_url + "/MetaApi/GetMetaApiSettings", {
            method: "GET",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            }
        });

        if (response.status === 200) {
            let data = await response.json();

            if (data.customerFragment === null) {
                return ProcessingResult.Ok(null);
            }
            else {
                return ProcessingResult.Ok(new CustomerFragmentDto
                    (
                        data.customerFragment.customerFragmentId,
                        data.customerFragment.fragment,
                        data.customerFragment.apiFragments.map((e: any) =>
                            new ApiFragmentDto
                                (
                                    e.apiFragmentId,
                                    e.fragment,
                                    e.swaggerVisible,
                                    e.pathFragments.map((f: any) =>
                                        new PathFragmentDto
                                            (
                                                f.pathFragmentId,
                                                f.methodName,
                                                f.pathId,
                                                f.pathName,
                                                {
                                                    ReadEnabled: f.readEnabled,
                                                    AddEnabled: f.addEnabled,
                                                    AddMultipleEnabled: f.addMultipleEnabled,
                                                    UpdateEnabled: f.updateEnabled,
                                                    UpdateMultipleEnabled: f.updateMultipleEnabled,
                                                    DeleteEnabled: f.deleteEnabled,
                                                    DeleteMultipleEnabled: f.deleteMultipleEnabled
                                                }
                                            )
                                    ),
                                    data.urlRoot + data.customerFragment.fragment + '/' + e.fragment + '/Swagger',
                                    e.customerApiCredentialIds
                                ),
                        )
                    )
                );
            }
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    CreateApiFragmentAsync: async function (customerFragmentId: number, fragment: string, swaggerVisible: boolean, customerApiCredentialIds: number[]): Promise<ProcessingResult<number>> {
        let authSession = await AuthToken.get();

        const response = await fetch(Settings.api_url + "/MetaApi/CreateApiFragment", {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            },
            body: JSON.stringify({
                'customerFragmentId': customerFragmentId,
                'fragment': fragment,
                'swaggerVisible': swaggerVisible,
                'customerApiCredentialIds': customerApiCredentialIds
            })
        });

        if (response.status === 200) {
            let data = await response.json();
            return ProcessingResult.Ok(data.id);
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    UpdateApiFragmentAsync: async function (apiFragmentId: number, customerFragmentId: number, fragment: string, swaggerVisible: boolean, customerApiCredentialIds: number[]): Promise<ProcessingResult<boolean>> {
        let authSession = await AuthToken.get();

        const response = await fetch(Settings.api_url + "/MetaApi/UpdateApiFragment", {
            method: "PUT",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            },
            body: JSON.stringify({
                'apiFragmentId': apiFragmentId,
                'customerFragmentId': customerFragmentId,
                'fragment': fragment,
                'swaggerVisible': swaggerVisible,
                'customerApiCredentialIds': customerApiCredentialIds
            })
        });

        if (response.status === 200) {
            return ProcessingResult.Ok(true);
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    DeleteApiFragmentAsync: async function (apiFragmentId: number): Promise<ProcessingResult<boolean>> {
        let authSession = await AuthToken.get();

        const response = await fetch(Settings.api_url + "/MetaApi/DeleteApiFragment?id=" + apiFragmentId, {
            method: "DELETE",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            }
        });

        if (response.status === 200) {
            return ProcessingResult.Ok(true);
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    CreatePathFragmentAsync: async function (apiFragmentId: number, methodName: string, pathId: number, operations: PathFragmentOperations): Promise<ProcessingResult<number>> {
        let authSession = await AuthToken.get();

        const response = await fetch(Settings.api_url + "/MetaApi/CreatePathFragment", {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            },
            body: JSON.stringify({
                'apiFragmentId': apiFragmentId,
                'methodName': methodName,
                'pathId': pathId,
                ...MetaApiApi.OperationsToBody(operations)
            })
        });

        if (response.status === 200) {
            let data = await response.json();
            return ProcessingResult.Ok(data.id);
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    UpdatePathFragmentAsync: async function (pathFragmentId: number, apiFragmentId: number, methodName: string, pathId: number, operations: PathFragmentOperations): Promise<ProcessingResult<boolean>> {
        let authSession = await AuthToken.get();

        const response = await fetch(Settings.api_url + "/MetaApi/UpdatePathFragment", {
            method: "PUT",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            },
            body: JSON.stringify({
                'pathFragmentId': pathFragmentId,
                'apiFragmentId': apiFragmentId,
                'methodName': methodName,
                'pathId': pathId,
                ...MetaApiApi.OperationsToBody(operations)
            })
        });

        if (response.status === 200) {
            return ProcessingResult.Ok(true);
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    DeletePathFragmentAsync: async function (pathFragmentId: number): Promise<ProcessingResult<boolean>> {
        let authSession = await AuthToken.get();

        const response = await fetch(Settings.api_url + "/MetaApi/DeletePathFragment?id=" + pathFragmentId, {
            method: "DELETE",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            }
        });

        if (response.status === 200) {
            return ProcessingResult.Ok(true);
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    }
};

export default MetaApiApi;